import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

export interface User {
  id: string;
  name?: string;
  email?: string;
  image?: string;
  token?: string;
  subscriptionExpiry: Date;
  subscriptionTier: string;
  tokenExpiry: Date;
  trialExpiry: Date;
  onboarding: boolean;
  qlickup: "admin" | "company" | "vendor" | "user" | "project";
  businessid?: number;
  permissions: any;
}

export interface UserSession {
  user: User;
}

interface SessionContextType {
  session: UserSession | null;
  loading: boolean;
  updateSession: (session: UserSession) => void;
  clearSession: () => void;
  navigateToProject: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  const [session, setSession] = useState<UserSession | null>(() => {
    const storedSession = localStorage.getItem("session");
    return storedSession ? JSON.parse(storedSession) : null;
  });
  const [loading, setLoading] = useState<boolean>(!session);

  useEffect(() => {
    if (!session) setLoading(false);
  }, [session]);

  const updateSession = (newSession: UserSession) => {
    setSession(newSession);
    localStorage.setItem("session", JSON.stringify(newSession));
  };

  const clearSession = () => {
    localStorage.removeItem("session");
    localStorage.removeItem("selectedBusiness");
    localStorage.removeItem("business_session");
    localStorage.removeItem("business_session_validated");
    setSession(null);
  };

  const navigateToProject = () => {
    switch (session.user.qlickup) {
      case "admin":
        navigate("/admin/login");
        break;
      case "vendor":
        navigate("/vendor/login");
        break;
      case "user":
        if (!session.user.onboarding) {
          navigate("/onboarding");
        } else {
          navigate("/dashboard");
        }
        break;
      case "project":
        navigate("/PM/MyCustomers");
        break;
      default:
        navigate("/onboarding");
        break;
    }
  };

  const value = useMemo(
    () => ({ session, loading, updateSession, clearSession, navigateToProject }),
    [session, loading]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};
